import { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import axios from 'axios';
import VotingABI from '../abi/Voting.json';
import CandidateImage from './CandidateImage';
import { parseContractError } from '../utils/parseError';

const CONTRACT_ADDRESS = process.env.REACT_APP_CONTRACT_ADDRESS;
const SEPOLIA_RPC_URL  = process.env.REACT_APP_SEPOLIA_RPC_URL;
const BACKEND_URL      = process.env.REACT_APP_BACKEND_URL;

export default function P05Vote({ walletAddress, onVotedClick, onEndedClick }) {
  const [candidates, setCandidates] = useState([]);
  const [totalVotes, setTotalVotes] = useState(0);
  const [isLoading, setLoading]     = useState(false);
  const [modalCandidate, setModal]  = useState(null);
  const [errorMsg, setErrorMsg]     = useState('');
  const pollRef = useRef(null);

  useEffect(() => { checkAndLoad(); return () => stopPoll(); }, []);

  async function checkAndLoad() {
    try {
      const provider = new ethers.JsonRpcProvider(SEPOLIA_RPC_URL);
      const contract = new ethers.Contract(CONTRACT_ADDRESS, VotingABI.abi, provider);
      if (walletAddress) {
        const voted = await contract.hasAddressVoted(walletAddress);
        if (voted) { onVotedClick(null); return; }
      }
      await loadCandidates();
      startPoll();
    } catch (e) { console.error('초기화 오류:', e); }
  }

  async function loadCandidates() {
    try {
      const provider = new ethers.JsonRpcProvider(SEPOLIA_RPC_URL);
      const contract = new ethers.Contract(CONTRACT_ADDRESS, VotingABI.abi, provider);
      const [onchain, total] = await Promise.all([
        contract.getCandidates(),
        contract.getTotalVotes(),
      ]);
      let offchain = [];
      try { offchain = await axios.get(`${BACKEND_URL}/candidates`).then(r => r.data); } catch {}
      const merged = onchain.map(c => {
        const off = offchain.find(o => o.id === Number(c.id)) || {};
        return { id: Number(c.id), name: c.name, voteCount: Number(c.voteCount),
          imageUrl: off.imageUrl || null, description: off.description || '' };
      });
      setCandidates(merged);
      setTotalVotes(Number(total));
    } catch (e) { console.error('데이터 로딩 오류:', e); }
  }

  function startPoll() {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const provider = new ethers.JsonRpcProvider(SEPOLIA_RPC_URL);
        const contract = new ethers.Contract(CONTRACT_ADDRESS, VotingABI.abi, provider);
        const state = await contract.getState();
        if (state === 2n) { stopPoll(); onEndedClick(); return; }
        const [list, total] = await Promise.all([
          contract.getCandidates(), contract.getTotalVotes(),
        ]);
        setCandidates(prev => prev.map(c => {
          const u = list.find(x => Number(x.id) === c.id);
          return u ? { ...c, voteCount: Number(u.voteCount) } : c;
        }));
        setTotalVotes(Number(total));
      } catch (e) { console.error('폴링 오류:', e); }
    }, 5000);
  }

  function stopPoll() {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }

  async function handleVote() {
    if (!modalCandidate) return;
    setModal(null); setLoading(true); setErrorMsg('');
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer   = await provider.getSigner();
      const contract = new ethers.Contract(CONTRACT_ADDRESS, VotingABI.abi, signer);
      const tx = await contract.vote(modalCandidate.id);
      await tx.wait();
      stopPoll(); onVotedClick(modalCandidate.id);
    } catch (err) {
      const msg = parseContractError(err);
      setErrorMsg(msg || '투표 중 오류가 발생했습니다.');
      if (!msg) console.error(err);
    } finally { setLoading(false); }
  }

  const maxVoteCount = candidates.length > 0 ? Math.max(...candidates.map(c => c.voteCount)) : 0;

  return (
    <div className="ev-page">
      {isLoading && (
        <div className="ev-overlay">
          <div className="ev-spinner" />
          <p className="ev-overlay-text">블록체인에 투표를 기록 중입니다...</p>
        </div>
      )}
      {modalCandidate && (
        <div className="ev-modal-backdrop">
          <div className="ev-modal">
            <h2 className="ev-modal-title">투표 확인</h2>
            <div className="ev-modal-body">
              <strong>{modalCandidate.name}</strong> 후보에게 투표하시겠습니까?<br />
              한 번 제출하면 취소할 수 없습니다.
            </div>
            <div className="ev-modal-actions">
              <button className="ev-btn ev-btn-ghost" onClick={() => setModal(null)}>취소</button>
              <button className="ev-btn ev-btn-primary" onClick={handleVote}>투표하기</button>
            </div>
          </div>
        </div>
      )}

      <div className="ev-page-header">
        <h1 className="ev-page-title">투표하기</h1>
        <p className="ev-page-subtitle">후보자를 선택하고 투표하세요. 한 번 투표하면 변경할 수 없습니다.</p>
        <div className="ev-total-count">
          현재 총 투표 수 <strong>{totalVotes}표</strong>
        </div>
      </div>

      {errorMsg && <div className="ev-error">{errorMsg}</div>}

      <div className="ev-candidate-grid">
        {candidates.map(c => {
          const pct = totalVotes > 0 ? ((c.voteCount / totalVotes) * 100).toFixed(1) : '0.0';
          const barW = maxVoteCount > 0 ? ((c.voteCount / maxVoteCount) * 100).toFixed(0) : 0;
          return (
            <div key={c.id} className="ev-vote-card" onClick={() => !isLoading && setModal({ id: c.id, name: c.name })}>
              <div className="ev-vote-card__num">기호 {c.id + 1}번</div>
              <CandidateImage
                src={c.imageUrl ? BACKEND_URL + c.imageUrl : null}
                alt={c.name}
                circle
              />
              <div className="ev-vote-card__name">{c.name}</div>
              {c.description && (
                <div className="ev-vote-card__desc">{c.description}</div>
              )}
              <div className="ev-vote-card__count">
                <strong>{c.voteCount}</strong>표 &nbsp;{pct}%
              </div>
              <div className="ev-progress-wrap">
                <div className="ev-progress-bar" style={{ width: `${barW}%` }} />
              </div>
              <button className="ev-btn ev-btn-primary" style={{ width: '100%', marginTop: 8 }}
                onClick={e => { e.stopPropagation(); if (!isLoading) setModal({ id: c.id, name: c.name }); }}
                disabled={isLoading}>
                투표하기
              </button>
            </div>
          );
        })}
      </div>
      <p style={{ marginTop: 20, fontSize: '0.8rem', color: 'var(--text-sub)' }}>
        * 한 번 투표하면 변경할 수 없습니다.
      </p>
    </div>
  );
}
